/**
 * Coordinates incremental avatar speech during LLM streaming: buffers sanitized
 * text, pushes speakable chunks to Anam, tracks stalls, and emits analytics.
 */
import type { TalkMessageStream } from '@anam-ai/js-sdk/dist/module/types/TalkMessageStream';
import {
  CHAT_WIDGET_AVATAR_SPEECH_COMPLETED,
  CHAT_WIDGET_AVATAR_SPEECH_STARTED,
} from '@/lib/analytics-events';
import { anamClient } from './anamClient';
import { createAvatarSpeechStallTracker } from './avatarSpeechStallTracker';
import { extractSpeakableChunk } from './extractSpeakableChunk';
import { isSpeechSanitizeStable } from './isSpeechSanitizeStable';
import { sanitizeForAvatarSpeech } from './sanitizeForAvatarSpeech';
import { waitForAvatarSpeakingIdle } from './waitForAvatarSpeakingIdle';

export { STALL_TOLERANCE_MS } from './avatarSpeechStallTracker';

export interface AvatarSpeechOrchestratorOptions {
  messageId: string;
  messageSentAt: number;
  institutionId: string;
  sessionId: string;
  track: (event: string, props: Record<string, unknown>) => void;
}

export interface AvatarSpeechOrchestrator {
  appendRawText: (text: string) => Promise<void>;
  complete: () => Promise<void>;
  fail: () => Promise<void>;
  abort: () => Promise<void>;
  dispose: () => void;
  getSpokenSanitizedLength: () => number;
}

function realignSpokenPrefix(spokenPrefix: string, fullSanitized: string): string {
  if (!spokenPrefix || fullSanitized.startsWith(spokenPrefix)) return spokenPrefix;
  let aligned = spokenPrefix;
  while (aligned.length > 0 && !fullSanitized.startsWith(aligned)) {
    aligned = aligned.slice(0, -1);
  }
  return aligned;
}

export function createAvatarSpeechOrchestrator(
  opts: AvatarSpeechOrchestratorOptions,
): AvatarSpeechOrchestrator {
  let rawAccumulated = '';
  let spokenSanitizedPrefix = '';
  let stream: TalkMessageStream | null = null;
  let chunkCount = 0;
  let speechStarted = false;
  let turnStartedAt: number | undefined;
  let streamOpen = true;

  const stallTracker = createAvatarSpeechStallTracker({
    track: opts.track,
    institutionId: opts.institutionId,
    sessionId: opts.sessionId,
    messageId: opts.messageId,
    isStreamOpen: () => streamOpen,
    isSpeechStarted: () => speechStarted,
  });

  const endActiveTurn = async () => {
    if (!stream) return;
    await anamClient.endTurn(stream);
    stream = null;
  };

  const pushExtractedChunks = async (forceFlush = false) => {
    while (true) {
      if (!forceFlush && !isSpeechSanitizeStable(rawAccumulated)) {
        stallTracker.schedule(chunkCount);
        return;
      }

      const fullSanitized = sanitizeForAvatarSpeech(rawAccumulated);
      spokenSanitizedPrefix = realignSpokenPrefix(spokenSanitizedPrefix, fullSanitized);
      const pending = fullSanitized.slice(spokenSanitizedPrefix.length);
      if (!pending.trim()) return;

      const extracted = extractSpeakableChunk(pending, {
        preferEarlyBreak: chunkCount === 0,
        forceFlush,
      });
      if (!extracted) {
        if (!forceFlush) stallTracker.schedule(chunkCount);
        return;
      }

      stallTracker.clear();
      const { chunk, remainder } = extracted;
      if (!chunk.trim()) return;

      if (!stream) {
        stream = anamClient.beginTurn(opts.messageId);
        turnStartedAt = Date.now();
      }

      await anamClient.pushChunk(stream, chunk, forceFlush && !remainder.trim());
      spokenSanitizedPrefix = fullSanitized.slice(0, fullSanitized.length - remainder.length);
      chunkCount += 1;

      if (!speechStarted) {
        speechStarted = true;
        opts.track(CHAT_WIDGET_AVATAR_SPEECH_STARTED, {
          institutionId: opts.institutionId,
          sessionId: opts.sessionId,
          messageId: opts.messageId,
          timeToFirstWordMs: Date.now() - opts.messageSentAt,
        });
      }

      if (remainder.trim()) {
        if (forceFlush) continue;
        stallTracker.schedule(chunkCount);
        return;
      }
      return;
    }
  };

  return {
    async appendRawText(text: string) {
      if (!text) return;
      rawAccumulated += text;
      await pushExtractedChunks(false);
    },

    async complete() {
      streamOpen = false;
      stallTracker.clear();
      await pushExtractedChunks(true);
      await endActiveTurn();
      if (speechStarted && turnStartedAt) {
        opts.track(CHAT_WIDGET_AVATAR_SPEECH_COMPLETED, {
          institutionId: opts.institutionId,
          sessionId: opts.sessionId,
          messageId: opts.messageId,
          totalDurationMs: Date.now() - turnStartedAt,
          chunkCount,
          stallCount: stallTracker.getStallCount(),
        });
      }
    },

    async fail() {
      streamOpen = false;
      stallTracker.clear();
      if (!speechStarted) {
        anamClient.interrupt();
        return;
      }
      await pushExtractedChunks(true);
      await endActiveTurn();
      await waitForAvatarSpeakingIdle();
    },

    async abort() {
      streamOpen = false;
      stallTracker.dispose();
      await endActiveTurn();
      anamClient.interrupt();
    },

    dispose() {
      streamOpen = false;
      stallTracker.dispose();
    },

    getSpokenSanitizedLength: () => spokenSanitizedPrefix.length,
  };
}
